import "./style.css";

import Hls from "hls.js";
import { useEffect, useRef, useState } from "react";
import { useObservable } from "react-use-observable";

import { useActive } from "../../../../../../hooks/active";
import { useBufferCatcher } from "../../../../../../hooks/buffer";
import { useBufferControl } from "../../../../../../hooks/bufferControl";
import { useBufferTime } from "../../../../../../hooks/bufferTime";
import { useChromecast } from "../../../../../../hooks/chromecast/chromecast";
import { useChromecastConnection } from "../../../../../../hooks/chromecast/chromecastConnection";
import { useHlsErrorCatcher } from "../../../../../../hooks/hlsErrorCatcher";
import { useHlsEventCatcher } from "../../../../../../hooks/hlsEventCatcher";
import { useLoading } from "../../../../../../hooks/loading";
import { useWatchedRanges } from "../../../../../../hooks/ranges";
import { useRemoteControl } from "../../../../../../hooks/remoteControl";
import { useReplay } from "../../../../../../hooks/replay";
import { useSubtitleSelector } from "../../../../../../hooks/subtitleSelector";
import { useTrack } from "../../../../../../hooks/track";
import { useVideoActions } from "../../../../../../hooks/videoActions";
import { useVideoEventCatcher } from "../../../../../../hooks/videoEventCatcher";
import { errorHandlerService } from "../../../../../../services/errorHandler";
import PlayerService from "../../../../../../services/player";
import { remoteControlService } from "../../../../../../services/remoteControl";
import RemoteControlIndicator from "./RemoteControlndicator";
import { useCustomPlayerContext } from "../../../../../../contexts/CustomPlayerContext";

interface IProps {
  playlistURL: string;
  seekTime: number;
  thumbnail: string;
  loop?: boolean;
  autoplay?: boolean;
  isMobile: boolean;
}

const Player: React.VFC<IProps> = ({
  playlistURL,
  seekTime,
  thumbnail,
  loop,
  autoplay,
  isMobile,
}) => {
  const { customPlayerPreferences } = useCustomPlayerContext();
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [hls] = useState(
    new Hls({
      startPosition: seekTime,
      capLevelToPlayerSize: true,
      nudgeMaxRetry: Number.MAX_SAFE_INTEGER,
      maxBufferLength: 60,
      maxBufferSize: 100 * 1000 * 1000,
      initialLiveManifestSize: 3,
      maxBufferHole: 1,
      fragLoadingRetryDelay: 1000,
      manifestLoadingRetryDelay: 2000,
      levelLoadingRetryDelay: 2000,
      startFragPrefetch: true,
    })
  );

  const playerServiceRef = useRef(new PlayerService(playerRef));
  const [remoteControlActive] = useObservable(
    () => remoteControlService.getRemoteControlMode(),
    []
  );

  useVideoEventCatcher(playerRef);
  useVideoActions({
    playerService: playerServiceRef,
    videoElementRef: playerRef,
    isFullscreenOptionEnabledForCustomPlayer: customPlayerPreferences.allowFullscreen,
    areMuteUnmuteOptionsEnabledForCustomPlayer: customPlayerPreferences.allowMuteUnmute,
    isPlaybackRateOptionEnabledForCustomPlayer: customPlayerPreferences.allowPlaybackSpeed,
    isSkipForwardEnabledForCustomPlayer: customPlayerPreferences.allowFastAdvance,
    isSkipBackwardsEnabledForCustomPlayer: customPlayerPreferences.allowFastBack,
  });
  useHlsEventCatcher(hls);
  useHlsErrorCatcher(hls);

  useWatchedRanges(playerRef);
  useLoading(playerRef);
  useBufferCatcher();
  useBufferTime(hls);
  useBufferControl(hls);
  useActive();
  useReplay();

  useChromecastConnection();
  useChromecast(playlistURL);
  useRemoteControl();

  useTrack(playerRef);
  useSubtitleSelector(playerRef);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement !== null) {
      if (Hls.isSupported()) {
        hls.loadSource(playlistURL);
        hls.attachMedia(playerElement);
      } else if (playerElement.canPlayType("application/vnd.apple.mpegurl")) {
        playerElement.src = playlistURL;
      } else {
        errorHandlerService.defineCurrentError({
          type: "api",
          data: errorHandlerService.getErrorDescriptionByErrorCode(601),
        });
      }
    }
  }, [hls, playlistURL]);

  return (
    <div
      className={`player__wrapper ${remoteControlActive ? "player__wrapper--active" : ""
        }`}
    >
      {isMobile ? (
        <video
          webkit-playsinline=""
          playsInline={true}
          x-webkit-airplay="allow"
          controlsList="nodownload"
          controls={true}
          poster={thumbnail}
          ref={playerRef}
          loop={loop}
          autoPlay={autoplay}
          muted={autoplay}
        ></video>
      ) : (
        <video
          controls={false}
          poster={thumbnail}
          ref={playerRef}
          loop={loop}
          autoPlay={autoplay}
          muted={autoplay}
        ></video>
      )}

      <RemoteControlIndicator thumbnail={thumbnail} />
    </div>
  );
};

export default Player;
